import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Bot, 
  Send, 
  X, 
  Maximize2, 
  Minimize2, 
  Mic, 
  MicOff, 
  Volume2, 
  VolumeX, 
  ArrowRight,
  Shield,
  GitPullRequest,
  ShoppingCart,
  Receipt,
  DollarSign,
  Building2,
  Scale,
  Package,
  FileSearch,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  Lock,
  Sparkles,
  RotateCcw,
  Pause,
  Play,
  Square
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import api from '../services/api';
import CopilotReportCard from './common/CopilotReportCard';

const ACTION_ICONS = {
  Shield,
  GitPullRequest,
  ShoppingCart,
  Receipt,
  DollarSign,
  Building2,
  Scale,
  Package,
  FileSearch,
  CheckCircle2,
  AlertTriangle,
  Lock,
  X
};

export default function AICopilotWidget() {
  const { user, isSuperAdmin, isAiEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdminPortal = isSuperAdmin || user?.type === 'SuperAdmin' || location.pathname.startsWith('/superadmin');
  const portalScope = isSuperAdminPortal ? 'superadmin' : (user?.type === 'Vendor' ? 'vendor_portal' : 'client_portal');

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);

  const { 
    speak, 
    cancel: stopSpeech, 
    pause: pauseSpeech, 
    resume: resumeSpeech, 
    togglePause: togglePauseSpeech, 
    isSpeaking: isTtsSpeaking, 
    isPaused: isTtsPaused, 
    isMuted, 
    toggleMute, 
    hasSupport: hasTTS 
  } = useSpeechSynthesis();

  const userName = user?.name || user?.email?.split('@')[0] || 'Executive';
  const userRole = (Array.isArray(user?.roles) ? user.roles[0]?.name || user.roles[0] : user?.role) || user?.type || 'User';

  const getInitialMessages = useCallback(() => {
    if (isSuperAdminPortal) {
      return [
        {
          role: 'assistant',
          content: `Hello ${userName}. I am **Savvy**, your **Platform AI Copilot** (SuperAdmin). I monitor multi-tenant platform infrastructure, cryptographic RSA licensing, system vitals, global security policies, and AI audit governance. How may I assist you today?`,
          actions: [
            { label: 'Control Tower', path: '/superadmin/control-tower', icon: 'Shield', primary: true },
            { label: 'Tenant Directory', path: '/superadmin/clients', icon: 'Building2' },
            { label: 'License Master', path: '/superadmin/licenses', icon: 'Lock' },
            { label: 'System Monitoring', path: '/superadmin/monitoring', icon: 'AlertTriangle' }
          ]
        }
      ];
    }
    return [
      {
        role: 'assistant',
        content: `Hello ${userName}. I am **Savvy**, your personalized **Enterprise AI Copilot** (${userRole}). You can speak with me or type questions. How may I assist you across your ERP workflows today?`,
        actions: [
          { label: 'Review Approvals', path: '/approvals/inbox', icon: 'GitPullRequest', primary: true },
          { label: 'Alert Rules', path: '/settings/system?tab=alerts', icon: 'AlertTriangle' },
          { label: 'AI Audit', path: '/settings/system?tab=ai-audit', icon: 'Shield' },
          { label: 'Trial Balance', path: '/reports/financial/trial-balance', icon: 'Scale' }
        ]
      }
    ];
  }, [userName, userRole, isSuperAdminPortal]);

  const [messages, setMessages] = useState(() => getInitialMessages());

  // Reset initial message if portal scope shifts (e.g. navigation into /superadmin)
  useEffect(() => {
    setMessages(getInitialMessages());
  }, [getInitialMessages]);

  // Reset conversation to initial state
  const handleReset = useCallback(() => {
    stopSpeech();
    setPendingAction(null);
    setQuery('');
    setMessages(getInitialMessages());
    toast.success('Savvy conversation reset to beginning', { id: 'savvy-reset', duration: 2500 });
  }, [stopSpeech, getInitialMessages]);

  // Main chat message submission
  const handleSend = async (customPrompt, options = {}) => {
    const textToSend = (typeof customPrompt === 'string' ? customPrompt : query).trim();
    if (!textToSend || loading) return;

    const isSpeechInput = Boolean(options.isSpeech || isListening);

    if (isListening) stopListening();

    const newMessages = [...messages, { role: 'user', content: textToSend }];
    setMessages(newMessages);
    setQuery('');
    setLoading(true);

    try {
      const res = await api.post('/intelligence/chat', {
        message: textToSend,
        context: {
          portal: portalScope,
          isSuperAdmin: isSuperAdminPortal,
          pendingAction: pendingAction || undefined,
          isSpeech: isSpeechInput,
          inputType: isSpeechInput ? 'speech' : (options.isAction ? 'action_confirm' : 'text')
        }
      });

      const reply = res?.data?.data?.reply || res?.data?.reply || 'Ledger analysis complete.';
      const actions = res?.data?.data?.actions || [];
      const reportData = res?.data?.data?.reportData || res?.data?.reportData || null;
      const triggerDownload = res?.data?.data?.triggerDownload || null;
      const emailSent = Boolean(res?.data?.data?.emailSent);
      const requiresConfirmation = Boolean(res?.data?.data?.requiresConfirmation);
      const returnedPendingAction = res?.data?.data?.pendingAction || null;
      const executedAction = res?.data?.data?.executedAction || null;
      const cancelledAction = Boolean(res?.data?.data?.cancelledAction);

      if (emailSent) {
        toast.success(`Report dispatched to ${res?.data?.data?.targetEmail || user?.email || 'your email'}`);
      }

      // Handle Action Confirmation / Cancellation state
      if (executedAction) {
        setPendingAction(null);
        setMessages([...newMessages, { 
          role: 'assistant', 
          content: reply, 
          actions: [],
          reportData,
          executedAction 
        }]);
        speak(reply);

        // Auto-navigate to confirmed destination
        if (executedAction.path) {
          setTimeout(() => {
            stopSpeech();
            setIsOpen(false);
            navigate(executedAction.path);
          }, 1200);
        }
        return;
      }

      if (cancelledAction) {
        setPendingAction(null);
      } else if (requiresConfirmation && returnedPendingAction) {
        setPendingAction(returnedPendingAction);
      }

      setMessages([...newMessages, { 
        role: 'assistant', 
        content: reply, 
        actions,
        reportData,
        requiresConfirmation,
        pendingAction: returnedPendingAction
      }]);
      speak(reply);
    } catch (err) {
      const isFeatureDisabled = err.response?.data?.code === 'AI_FEATURE_DISABLED';
      const fallback = isFeatureDisabled
        ? (err.response?.data?.error || `Savvy AI Copilot is currently disabled for your organization. It is a paid enterprise feature. Please contact your Administrator to activate your subscription.`)
        : `I have logged your request. You can inspect approvals, open invoices, or view live audit trails using your role permissions.`;

      setMessages([...newMessages, { 
        role: 'assistant', 
        content: fallback,
        actions: isFeatureDisabled ? [] : [
          { label: 'Approvals Inbox', path: '/approvals/inbox', icon: 'GitPullRequest', primary: true },
          { label: 'Audit Trail', path: '/reports/admin/audit-trail', icon: 'Shield' }
        ]
      }]);
      speak(fallback);
    } finally {
      setLoading(false);
    }
  };

  // VAD Speech Recognition with Pause Understanding:
  // Once user stops speaking and pauses for 1.3s, automatically follow instruction and take action!
  const { isListening, isPaused, transcript, startListening, stopListening, hasSupport: hasSTT } = useSpeechRecognition({
    onSpeechEnd: (spokenText) => {
      if (spokenText && spokenText.trim()) {
        handleSend(spokenText.trim(), { isSpeech: true });
      }
    },
    pauseDuration: 1300
  });

  // Sync STT transcript into query input
  useEffect(() => {
    if (transcript) {
      setQuery(transcript);
    }
  }, [transcript]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleMicToggle = () => {
    if (isListening) {
      stopListening();
      if (query.trim()) {
        handleSend(query, { isSpeech: true });
      }
    } else {
      stopSpeech();
      startListening();
    }
  };

  const handleActionClick = (act) => {
    if (act.isConfirm) {
      handleSend("Yes, proceed", { isAction: true });
    } else if (act.isCancel) {
      handleSend("Cancel", { isAction: true });
    } else if (act.path) {
      stopSpeech();
      setIsOpen(false);
      navigate(act.path);
    }
  };

  // If user is not logged in, don't show widget
  if (!user) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* 1. Floating Launcher Button */}
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            stopSpeech();
          }}
          className={clsx(
            "group relative flex items-center gap-2.5 px-4 py-3 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer border",
            isAiEnabled
              ? "bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white border-indigo-400/30"
              : "bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 text-slate-200 border-purple-500/30"
          )}
          title={isAiEnabled ? "Open Savvy AI (Voice & Chat with UK Accent)" : "Savvy AI (Paid Feature - Contact Admin to activate)"}
        >
          <div className="relative">
            <Bot className={clsx("w-5 h-5", isAiEnabled ? "text-white animate-pulse" : "text-purple-300")} />
            {isAiEnabled ? (
              <>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-indigo-900 animate-ping" />
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-indigo-900" />
              </>
            ) : (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full ring-2 ring-slate-900" />
            )}
          </div>
          <span className="text-xs font-black tracking-wide pr-1">
            Savvy AI
          </span>
          <span className={clsx(
            "text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider backdrop-blur-xs",
            isAiEnabled
              ? "bg-white/20 text-white"
              : "bg-amber-400/20 text-amber-300 border border-amber-400/30"
          )}>
            {isAiEnabled ? "Live" : "Paid"}
          </span>
        </button>
      )}

      {/* 2. Chat Modal / Drawer */}
      {isOpen && (
        <div className={clsx(
          "bg-white rounded-2xl shadow-2xl border border-slate-200/90 flex flex-col overflow-hidden transition-all duration-300 animate-in fade-in slide-in-from-bottom-4",
          isExpanded 
            ? "w-[92vw] sm:w-[600px] h-[80vh] max-h-[750px]" 
            : "w-[92vw] sm:w-[420px] h-[540px]"
        )}>
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-4 text-white flex items-center justify-between relative">
            <div className="flex items-center gap-3">
              <div className="relative p-2 rounded-xl bg-indigo-600/30 border border-indigo-400/40 text-indigo-300">
                <Bot className="w-5 h-5" />
                {isTtsSpeaking && (
                  <span className="absolute -bottom-1 -right-1 flex h-2.5 w-2.5">
                    <span className={clsx("absolute inline-flex h-full w-full rounded-full opacity-75", isTtsPaused ? "bg-amber-400" : "animate-ping bg-emerald-400")} />
                    <span className={clsx("relative inline-flex rounded-full h-2.5 w-2.5", isTtsPaused ? "bg-amber-500" : "bg-emerald-500")} />
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-black tracking-tight">Savvy • AI Copilot</h3>
                  <span className={clsx(
                    "text-[10px] font-bold px-1.5 py-0.5 rounded border",
                    isAiEnabled
                      ? (isTtsSpeaking ? (isTtsPaused ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 animate-pulse") : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30")
                      : "bg-amber-500/20 text-amber-300 border-amber-500/30"
                  )}>
                    {isAiEnabled ? (isTtsSpeaking ? (isTtsPaused ? "Paused" : "Speaking") : "UK Voice") : "Paid Add-On"}
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 font-medium">
                  {userName} • <span className="text-indigo-300 font-bold">{userRole}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* Header Quick Voice Pause / Stop Controls when speaking */}
              {isTtsSpeaking && hasTTS && (
                <div className="flex items-center gap-0.5 mr-1 bg-white/10 px-1.5 py-0.5 rounded-lg border border-white/10 backdrop-blur-xs">
                  <button
                    type="button"
                    onClick={togglePauseSpeech}
                    className="p-1 rounded text-amber-300 hover:text-white hover:bg-white/20 transition-colors"
                    title={isTtsPaused ? "Resume speaking" : "Pause speech"}
                  >
                    {isTtsPaused ? <Play className="w-3.5 h-3.5 fill-current" /> : <Pause className="w-3.5 h-3.5 fill-current" />}
                  </button>
                  <button
                    type="button"
                    onClick={stopSpeech}
                    className="p-1 rounded text-rose-300 hover:text-white hover:bg-white/20 transition-colors"
                    title="Stop speaking"
                  >
                    <Square className="w-3.5 h-3.5 fill-current" />
                  </button>
                </div>
              )}

              {/* Reset Conversation Button */}
              <button
                type="button"
                onClick={handleReset}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                title="Reset conversation to beginning"
              >
                <RotateCcw className="w-4 h-4" />
              </button>

              {/* TTS Mute Toggle */}
              {hasTTS && isAiEnabled && (
                <button
                  type="button"
                  onClick={toggleMute}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                  title={isMuted ? "Unmute Voice" : "Mute Voice"}
                >
                  {isMuted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
                </button>
              )}

              {/* Expand / Minimize */}
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                title={isExpanded ? "Restore size" : "Expand"}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>

              {/* Close Button */}
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  stopSpeech();
                  if (isListening) stopListening();
                }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Paid Feature Locked Screen (If Admin has disabled AI for this tenant) */}
          {!isAiEnabled ? (
            <div className="flex-1 p-6 flex flex-col items-center justify-center text-center bg-slate-50/70">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-300 text-purple-700 flex items-center justify-center mb-4 shadow-sm">
                <Sparkles className="w-7 h-7 animate-pulse" />
              </div>

              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-purple-100 border border-purple-200 text-purple-800 text-[10px] font-extrabold uppercase tracking-wider mb-2">
                <Lock className="w-3 h-3" />
                <span>Paid Enterprise Add-On</span>
              </div>

              <h4 className="text-base font-black text-slate-900 mb-1">
                Savvy AI is Disabled
              </h4>
              <p className="text-xs text-slate-600 max-w-xs leading-relaxed mb-5 font-medium">
                Savvy AI Copilot is an enterprise paid module controlled by Platform Administrators. It is currently not enabled for your tenant organization.
              </p>
              
              <div className="w-full bg-white p-3.5 rounded-xl border border-slate-200/80 text-left text-xs space-y-2.5 mb-5 shadow-xs">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Features unlocked upon activation:
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-semibold text-[11px]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>British Accent Speech & Pause Understanding</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-semibold text-[11px]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Multi-Column Trial Balance & Ledger Intelligence</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-semibold text-[11px]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Interactive Action Reconfirmation & Navigation</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-semibold text-[11px]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Strict RBAC Authorization & Multi-Tenant Isolation</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/settings/system?tab=billing');
                }}
                className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <span>Contact Admin / View Subscription</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Quick Shortcuts Bar */}
              <div className="bg-slate-50 px-3.5 py-2 border-b border-slate-100 flex items-center gap-2 overflow-x-auto stylish-scrollbar">
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-[11px] font-bold whitespace-nowrap px-2.5 py-1 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors shrink-0 flex items-center gap-1 cursor-pointer shadow-2xs"
                  title="Reset conversation to beginning"
                >
                  <RotateCcw className="w-3 h-3 text-slate-600" />
                  <span>Reset</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSend("Show the trial balance")}
                  className="text-[11px] font-bold whitespace-nowrap px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors shrink-0 cursor-pointer"
                >
                  Trial Balance
                </button>
                <button
                  type="button"
                  onClick={() => handleSend("What are today's top executive priorities?")}
                  className="text-[11px] font-bold whitespace-nowrap px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors shrink-0 cursor-pointer"
                >
                  Today's Priorities
                </button>
                <button
                  type="button"
                  onClick={() => handleSend("Where are we overspending on budget?")}
                  className="text-[11px] font-bold whitespace-nowrap px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors shrink-0 cursor-pointer"
                >
                  Budget Health
                </button>
                <button
                  type="button"
                  onClick={() => handleSend("Which invoices are pending payment?")}
                  className="text-[11px] font-bold whitespace-nowrap px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors shrink-0 cursor-pointer"
                >
                  Pending Invoices
                </button>
                <button
                  type="button"
                  onClick={() => handleSend("Check low stock inventory items")}
                  className="text-[11px] font-bold whitespace-nowrap px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors shrink-0 cursor-pointer"
                >
                  Low Stock Items
                </button>
              </div>

          {/* Messages Area */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3.5 bg-slate-50/50 stylish-scrollbar text-xs">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={clsx(
                  "flex flex-col gap-1.5",
                  m.role === 'user' ? "items-end" : "items-start"
                )}
              >
                <div
                  className={clsx(
                    "p-3 rounded-2xl max-w-[92%] leading-relaxed whitespace-pre-line shadow-xs font-medium",
                    m.role === 'user'
                      ? "bg-indigo-600 text-white rounded-tr-none"
                      : "bg-white text-slate-800 border border-slate-200/80 rounded-tl-none"
                  )}
                >
                  <div>{m.content}</div>
                  {m.reportData && (
                    <CopilotReportCard reportData={m.reportData} />
                  )}
                </div>

                {/* Confirmation Prompt Card */}
                {m.requiresConfirmation && (
                  <div className="ml-1 p-2.5 rounded-xl bg-amber-50 border border-amber-200 max-w-[90%] flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="text-[11px] font-bold text-amber-800">
                      Savvy will execute this action upon your confirmation. Say "Yes" or click below.
                    </span>
                  </div>
                )}

                {/* Assistant Message Actions & Voice Controls */}
                {m.role === 'assistant' && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5 max-w-[95%]">
                    {/* Replay / Pause / Stop Voice inline */}
                    {hasTTS && !isMuted && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (isTtsSpeaking) {
                              stopSpeech();
                            } else {
                              speak(m.content);
                            }
                          }}
                          className={clsx(
                            "inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border transition-colors cursor-pointer",
                            isTtsSpeaking
                              ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                              : "text-slate-500 hover:text-indigo-600 bg-white border-slate-200 hover:bg-slate-50"
                          )}
                          title={isTtsSpeaking ? "Stop speech" : "Listen to this response"}
                        >
                          {isTtsSpeaking ? (
                            <>
                              <Square className="w-3 h-3 fill-current text-rose-600" />
                              <span>Stop</span>
                            </>
                          ) : (
                            <>
                              <Volume2 className="w-3 h-3 text-indigo-600" />
                              <span>Listen</span>
                            </>
                          )}
                        </button>

                        {isTtsSpeaking && (
                          <button
                            type="button"
                            onClick={togglePauseSpeech}
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer"
                            title={isTtsPaused ? "Resume speech" : "Pause speech"}
                          >
                            {isTtsPaused ? (
                              <>
                                <Play className="w-3 h-3 fill-current text-amber-600" />
                                <span>Resume</span>
                              </>
                            ) : (
                              <>
                                <Pause className="w-3 h-3 fill-current text-amber-600" />
                                <span>Pause</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* RBAC Action Buttons / Confirmation Buttons */}
                    {Array.isArray(m.actions) && m.actions.map((act, aIdx) => {
                      const Icon = ACTION_ICONS[act.icon] || ArrowRight;
                      return (
                        <button
                          key={aIdx}
                          onClick={() => handleActionClick(act)}
                          className={clsx(
                            "inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-lg border transition-all hover:scale-102 cursor-pointer shadow-xs",
                            act.isConfirm || act.primary
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700"
                              : act.isCancel
                                ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"
                                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-indigo-600"
                          )}
                        >
                          <Icon className="w-3 h-3" />
                          <span>{act.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium italic">
                <Bot className="w-4 h-4 animate-spin text-indigo-600" />
                <span>Savvy is analysing live database ledgers...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* TTS Active Speaking & Pause / Stop Banner */}
          {isTtsSpeaking && hasTTS && (
            <div className={clsx(
              "px-3.5 py-2 border-t flex items-center justify-between transition-all",
              isTtsPaused
                ? "bg-amber-50/90 border-amber-200 text-amber-900"
                : "bg-indigo-50/90 border-indigo-200 text-indigo-950"
            )}>
              <div className="flex items-center gap-2 text-xs font-bold">
                {isTtsPaused ? (
                  <>
                    <div className="flex items-center gap-0.5">
                      <span className="w-1.5 h-3 bg-amber-500 rounded-xs" />
                      <span className="w-1.5 h-3 bg-amber-500 rounded-xs" />
                    </div>
                    <span>Savvy speech paused</span>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <span className="w-1 h-3 bg-indigo-600 rounded-full animate-pulse" />
                      <span className="w-1 h-4 bg-indigo-600 rounded-full animate-pulse [animation-delay:150ms]" />
                      <span className="w-1 h-2.5 bg-indigo-600 rounded-full animate-pulse [animation-delay:300ms]" />
                      <span className="w-1 h-4 bg-indigo-600 rounded-full animate-pulse [animation-delay:75ms]" />
                    </div>
                    <span className="text-indigo-900">Savvy is speaking (British Accent)...</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                {/* Pause / Resume button */}
                <button
                  type="button"
                  onClick={togglePauseSpeech}
                  className={clsx(
                    "inline-flex items-center gap-1 text-[11px] font-extrabold px-2.5 py-1 rounded-lg border shadow-2xs transition-colors cursor-pointer",
                    isTtsPaused
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700"
                      : "bg-amber-500 hover:bg-amber-600 text-white border-amber-600"
                  )}
                  title={isTtsPaused ? "Resume speaking" : "Pause speech"}
                >
                  {isTtsPaused ? (
                    <>
                      <Play className="w-3 h-3 fill-current" />
                      <span>Resume</span>
                    </>
                  ) : (
                    <>
                      <Pause className="w-3 h-3 fill-current" />
                      <span>Pause</span>
                    </>
                  )}
                </button>

                {/* Stop button */}
                <button
                  type="button"
                  onClick={stopSpeech}
                  className="inline-flex items-center gap-1 text-[11px] font-extrabold px-2.5 py-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white border border-rose-700 shadow-2xs transition-colors cursor-pointer"
                  title="Stop speech"
                >
                  <Square className="w-3 h-3 fill-current" />
                  <span>Stop</span>
                </button>
              </div>
            </div>
          )}

          {/* STT Listening & Intelligent Pause Banner */}
          {isListening && (
            <div className={clsx(
              "px-4 py-2 border-t flex items-center justify-between transition-colors",
              isPaused 
                ? "bg-emerald-50 border-emerald-200" 
                : "bg-rose-50 border-rose-200 animate-pulse"
            )}>
              <div className="flex items-center gap-2 text-xs font-bold">
                {isPaused ? (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                    <span className="text-emerald-800">Pause detected — Executing your instruction...</span>
                  </>
                ) : (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-600 animate-ping" />
                    <span className="text-rose-700">Listening... (Pause naturally to execute)</span>
                  </>
                )}
              </div>
              <button
                onClick={stopListening}
                className="text-[11px] font-bold text-slate-600 underline cursor-pointer"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Input Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="p-3 bg-white border-t border-slate-100 flex items-center gap-2"
          >
            {/* STT Microphone Button */}
            {hasSTT && (
              <button
                type="button"
                onClick={handleMicToggle}
                className={clsx(
                  "p-2 rounded-xl border transition-all cursor-pointer shrink-0 shadow-xs",
                  isListening
                    ? "bg-rose-600 text-white border-rose-700 animate-pulse"
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-indigo-600"
                )}
                title={isListening ? "Stop listening" : "Voice input (UK Accent)"}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}

            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isListening ? "Listening... (Pause naturally to execute)" : "Speak or ask Savvy about reports, approvals, orders..."}
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
            />

            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="p-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-xl transition-colors cursor-pointer shrink-0 shadow-xs"
              title="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          </>
          )}
        </div>
      )}
    </div>
  );
}
